import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Header } from "./components/Header";
import { ThemeProvider } from "./components/ThemeProvider";
import { Toaster } from "./components/ui/sonner";
import { PageLoader } from "./components/LoadingSpinner";
import { HeroSection } from "./components/HeroSection";
import { VideoHeroSection } from "./components/VideoHeroSection";
import { LazySection } from "./components/LazySection";
import { ArrowUp } from "lucide-react";

const statisticsSectionLoader = () =>
  import("./components/StatisticsSection").then(module => ({ default: module.StatisticsSection }));

const pricingSectionLoader = () =>
  import("./components/PricingSection").then(module => ({ default: module.PricingSection }));

const showcaseSectionLoader = () =>
  import("./components/ShowcaseSection").then(module => ({ default: module.ShowcaseSection }));

const featuresSectionLoader = () =>
  import("./components/FeaturesSection").then(module => ({ default: module.FeaturesSection }));

const contactSectionLoader = () =>
  import("./components/ContactSection").then(module => ({ default: module.ContactSection }));

const footerLoader = () =>
  import("./components/Footer").then(module => ({ default: module.Footer }));

const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfUsePage = lazy(() => import("./pages/TermsOfUsePage"));

function MobileBackToTop() {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const handleScroll = () => {
      setIsVisible(window.scrollY > 200);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMounted]);

  if (!isMounted) {
    return null;
  }

  return createPortal(
    <div
      className="sm:hidden fixed bottom-5 left-4 pointer-events-none"
      style={{ zIndex: 2147483647 }}
    >
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
        className={`pointer-events-auto group flex items-center justify-center rounded-full border border-white/30 bg-black/45 text-white backdrop-blur-2xl shadow-[0_18px_52px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.22)] transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 light:bg-black/65 light:border-black/45 light:shadow-[0_20px_58px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.22)] ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
        style={{ width: "44px", height: "44px" }}
        aria-label="Back to top"
      >
        <ArrowUp className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5" />
      </button>
    </div>,
    document.body
  );
}

function HomeContent() {
  return (
    <>
      {/* Hero section loads immediately as it's critical above-the-fold content */}
      <HeroSection />

      <VideoHeroSection />

      {/* Other sections are lazily hydrated when approaching the viewport */}
      <div id="statistics-anchor" style={{ scrollMarginTop: "6rem" }} />
      <LazySection
        loader={statisticsSectionLoader}
        rootMargin="400px"
        fallback={
          <div className="h-96 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Зарежда статистики...</div>
          </div>
        }
      />

      <div id="pricing-anchor" style={{ scrollMarginTop: "6rem" }} />
      <LazySection
        loader={pricingSectionLoader}
        rootMargin="400px"
        fallback={
          <div className="h-96 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Зарежда цени...</div>
          </div>
        }
      />

      <div id="showcase-anchor" style={{ scrollMarginTop: "6rem" }} />
      <LazySection
        loader={showcaseSectionLoader}
        rootMargin="400px"
        fallback={
          <div className="h-96 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Зарежда галерия...</div>
          </div>
        }
      />

      <div id="features-anchor" style={{ scrollMarginTop: "6rem" }} />
      <LazySection
        loader={featuresSectionLoader}
        rootMargin="400px"
        fallback={
          <div className="h-96 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Зарежда функции...</div>
          </div>
        }
      />

      <div id="contact-anchor" style={{ scrollMarginTop: "6rem" }} />
      <LazySection
        loader={contactSectionLoader}
        rootMargin="400px"
        fallback={
          <div className="h-96 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Зарежда контакти...</div>
          </div>
        }
      />
    </>
  );
}

export default function App() {
  const location = useLocation();
  const hasRestoredScrollRef = useRef(false);

  useEffect(() => {
    const isIOS = (() => {
      if (typeof navigator === "undefined") {
        return false;
      }

      const ua = navigator.userAgent || "";
      const iOSDevice = /iPad|iPhone|iPod/.test(ua);
      const maxTouchPoints = (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints ?? 0;
      const iPadOS13Plus = navigator.platform === "MacIntel" && maxTouchPoints > 1;
      return Boolean(iOSDevice || iPadOS13Plus);
    })();

    if (isIOS) {
      document.documentElement.classList.add("ios");
    }

    const preventGesture = (event: Event) => {
      event.preventDefault();
    };

    const preventMultiTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    if (isIOS) {
      document.addEventListener("touchmove", preventMultiTouchMove, { passive: false });
      document.addEventListener("gesturestart", preventGesture as EventListener, { passive: false } as AddEventListenerOptions);
      document.addEventListener("gesturechange", preventGesture as EventListener, { passive: false } as AddEventListenerOptions);
      document.addEventListener("gestureend", preventGesture as EventListener, { passive: false } as AddEventListenerOptions);
    }

    let rafId: number | null = null;
    const persistScroll = () => {
      if (rafId !== null) {
        return;
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        try {
          sessionStorage.setItem("advelo-scroll-y", String(window.scrollY));
        } catch {
          // ignore
        }
      });
    };

    window.addEventListener("scroll", persistScroll, { passive: true });
    window.addEventListener("pagehide", persistScroll);

    return () => {
      window.removeEventListener("scroll", persistScroll);
      window.removeEventListener("pagehide", persistScroll);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }

      if (isIOS) {
        document.removeEventListener("touchmove", preventMultiTouchMove);
        document.removeEventListener("gesturestart", preventGesture as EventListener);
        document.removeEventListener("gesturechange", preventGesture as EventListener);
        document.removeEventListener("gestureend", preventGesture as EventListener);
      }
    };
  }, []);

  useEffect(() => {
    const state = (location.state as { scrollTo?: string; triggerAt?: number } | null) ?? {};
    let targetId = state.scrollTo;

    if (!targetId && location.pathname === "/") {
      try {
        const storedTarget = sessionStorage.getItem("advelo-scroll-target");
        if (storedTarget) {
          targetId = storedTarget;
          sessionStorage.removeItem("advelo-scroll-target");
        }
      } catch (error) {
        console.warn("Неуспешно извличане на целевата секция от sessionStorage", error);
      }
    }

    requestAnimationFrame(() => {
      if (targetId && location.pathname === "/") {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          return;
        }
      }

      let navType: string | null = null;
      try {
        const entry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
        navType = entry?.type ?? null;
      } catch {
        navType = null;
      }

      if (!targetId && !hasRestoredScrollRef.current && location.pathname === "/" && navType && navType !== "navigate") {
        try {
          const storedY = sessionStorage.getItem("advelo-scroll-y");
          const y = storedY ? Number(storedY) : NaN;
          if (Number.isFinite(y) && y > 0) {
            hasRestoredScrollRef.current = true;
            sessionStorage.removeItem("advelo-scroll-y");
            window.scrollTo({ top: y, left: 0, behavior: "auto" });
            return;
          }
        } catch {
          // ignore
        }
      }

      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    });

  }, [location]);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
          {/* Header is loaded immediately as it's above the fold */}
          <Header />

          <main id="main-content" className="relative">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomeContent />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-use" element={<TermsOfUsePage />} />
              </Routes>
            </Suspense>
          </main>

          <LazySection
            loader={footerLoader}
            rootMargin="400px"
            fallback={<div className="h-32 bg-secondary/20 animate-pulse" />}
          />

          {/* Toast notifications */}
          <Toaster 
            position="top-right"
            className="bg-card border-border"
            closeButton
            richColors
            toastOptions={{
              className: "bg-card border-border text-card-foreground",
              duration: 5000,
            }}
          />

          <MobileBackToTop />
        </div>
      </ThemeProvider>
    </ErrorBoundary>
  );
}